import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Head } from 'common/components/Head';
import { Company } from 'types/company';
import { Meta } from 'types/meta';
import ReactPixel from 'react-facebook-pixel';
import { Framework } from 'client/components/Framework';
import { getTranslation } from 'localization/helpers';
import { CompanyName } from 'styles/CompanyName';
import reviewIcon from 'images/reviewIcon.svg';
import arrowIcon from 'images/arrowIcon.svg';
import { MenuBlock } from 'client/components/MenuBlock';
import { NavigationButton } from 'client/components/NavigationButtons/NavigationButton';
import { Pad } from 'styles/Pad';
import { QrType } from 'types/qrType';
import { StyledLink } from 'styles/Form/StyledLink';
import { SocialNetworks } from 'client/components/SocialNetworks';
import { renderButton } from 'client/components/NavigationButtons/LinkButton';

export const CompanyMainPage: FC<{
  company: Company;
  meta: Meta;
  qrType: QrType;
  hash: string;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
}> = ({ company, meta, qrType, hash }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    let redirect = true;
    if (
      company.menu.length > 0 ||
      company.actions.length > 0 ||
      company.social_networks.length > 0
    )
      redirect = false;
    if (redirect) navigate('feedback');
    ReactPixel.init(company.facebook_pixel);
    ReactPixel.pageView();
  }, []);

  const companyName = getTranslation(
    company.translations,
    'title',
    i18n.language,
    meta.default_locale
  );

  const notification = getTranslation(
    company.translations,
    'notification',
    i18n.language,
    meta.default_locale
  );
  const metaTitle = `${t('mainPage')} – ${companyName}`;

  return (
    <Framework locales={meta.locales} logo={meta.logo} color={meta.color}>
      <Head title={metaTitle} description={metaTitle} />
      {companyName && <CompanyName>{companyName}</CompanyName>}
      <MenuBlock menus={company.menu} meta={meta} />
      <StyledLink to={'feedback'}>
        <NavigationButton
          text={t('giveFeedback')}
          color={meta.color}
          icon={reviewIcon}
          arrow={arrowIcon}
        />
      </StyledLink>
      {company.actions.map((button, index) =>
        renderButton(button, index, meta, {
          act_id: button.id.toString(),
          location_hash: hash
        })
      )}
      {company.social_networks.length > 0 && (
        <SocialNetworks networks={company.social_networks} color={meta.color} />
      )}
      {notification && <Pad>{notification}</Pad>}
    </Framework>
  );
};
